import { ButtonComponent } from '@/components/commons'
import { Text } from '@/components/commons/typography/Text'
import { Title } from '@/components/commons/typography/Title'
import { EyeIcon } from '@/public/assets/v2/images/icons'
import { cssClass } from '@/shared/utils'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DealSyncContactsInvited } from './DealSyncContactsInvited'
import { DealSyncContactsModal } from './DealSyncContactsModal'
import {
  useDealSyncInvite,
  useDelSyncContacts,
  useDelSyncStatus,
} from './hooks/deal-sync'
import { DealSyncSideBarModalSyncing } from './SbModalSyncing'
import { useDealSyncStore } from './store/dealSync'
import styles from './styles/deal-sync.module.scss'
import { DealSyncContact } from './types/deal-sync'

export const DealSyncConnected = () => {
  const router = useRouter()
  const { data: dealSyncing } = useDelSyncStatus()
  const { data, isLoading } = useDelSyncContacts()
  const {
    isModalOpen: contactsModalOpen,
    setIsModalOpen: setContactsModalOpen,
    isSyncingModalOpen,
    setIsSyncingModalOpen,
    isContactsInvitedModalOpen,
    setIsContactsInvitedModalOpen,
  } = useDealSyncStore()
  const [contactsInvited, setContactsInvited] = useState(false)
  const { mutateAsync, isLoading: invitingContacts } = useDealSyncInvite()
  const contacts = useMemo(() => {
    if (isLoading) return []

    return data
  }, [data, isLoading])

  const handleInviteContacts = useCallback(
    async (contacts: DealSyncContact[]) => {
      setContactsInvited(true)
      if (contacts.length === 0) {
        setContactsModalOpen(false)
        return
      }
      const emailsString = contacts
        .map((contact) => contact.contactEmail)
        .join(',')
      await mutateAsync(emailsString)
      setContactsInvited(true)
    },
    [mutateAsync, setContactsInvited, setContactsModalOpen]
  )

  useEffect(() => {
    const { action } = router.query
    if (action) {
      // Handle the action parameter here
      if (action === 'vip-invite') {
        if (contacts.length > 0) {
          setContactsModalOpen(true)
        } else {
          console.log('open syncing modal')
          setIsSyncingModalOpen(true)
        }

        // Remove the action parameter from the URL
        router.replace(router.pathname, router.asPath.split('?')[0], {
          shallow: true,
        })
      } else if (action === 'vip-invite-all') {
        if (contacts.length > 0) {
          handleInviteContacts(contacts)
          setIsContactsInvitedModalOpen(true)
        } else {
          setIsSyncingModalOpen(true)
        }
        router.replace(router.pathname, router.asPath.split('?')[0], {
          shallow: true,
        })
      }
    }
  }, [
    setContactsModalOpen,
    contacts,
    router,
    setIsSyncingModalOpen,
    setIsContactsInvitedModalOpen,
    handleInviteContacts,
  ])

  const title = useMemo(() => {
    if (contacts.length > 0) {
      const connectionsText = contacts.length > 1 ? 'connections' : 'connection'
      return `${contacts.length} VIP ${connectionsText} Identified`
    }
    if (dealSyncing) {
      return '🚀 Syncing Your VIP Network…'
    }
    return 'No New VIP Contacts Found...Yet!'
  }, [contacts.length, dealSyncing])

  const description = useMemo(() => {
    if (contacts.length > 0) {
      const connectionsText = contacts.length > 1 ? 'connections' : 'connection'
      return `We've found ${contacts.length} VIP ${connectionsText} from your email network—collaborators, creators, and deal-related contacts who can help you unlock new opportunities. Strengthen your network, foster relationships, and stay top of mind with key players in your career growth.`
    }
    if (dealSyncing) {
      return "We're uncovering top collaborators, creators, and deal-makers from your inbox. Your VIP connections are on the way!"
    }
    return undefined
  }, [contacts.length, dealSyncing])

  const closeModal = () => {
    setContactsModalOpen(false)
    setContactsInvited(false)
  }

  return (
    <>
      <DealSyncContactsModal
        contacts={contacts}
        isOpen={contactsModalOpen}
        onClose={closeModal}
        onInvite={handleInviteContacts}
        isLoading={invitingContacts}
        contactsInvited={contactsInvited}
      />
      <DealSyncSideBarModalSyncing
        isOpen={isSyncingModalOpen}
        onClose={() => setIsSyncingModalOpen(false)}
      />
      <DealSyncContactsInvited
        isOpen={isContactsInvitedModalOpen}
        onClose={() => setIsContactsInvitedModalOpen(false)}
      />
      <div
        className={cssClass([
          `p-16 bg-white rounded-20 flex flex-column gap-14 items-center`,
          dealSyncing
            ? styles['deal-sync-card-connected']
            : styles['deal-sync-card'],
        ])}
      >
        <div
          className="relative"
          style={{
            aspectRatio: '32 / 6',
            margin: '0 auto',
            height: '40px',
          }}
        >
          <Image
            src="/assets/creatorland-deal-sync-logo-2.png"
            alt="Logo Sync"
            style={{
              objectFit: 'contain',
              width: '100%',
            }}
            layout="fill"
          />
        </div>
        <Title
          sizeClass="text-15"
          className="text-center m-0"
          weightClass="font-bold"
          lineHeightClass="leading-23"
        >
          {title}
        </Title>
        {description ? (
          <Text variation="small" className="text-center" sizeClass="text-13">
            {description}
          </Text>
        ) : (
          <Text variation="small" className="text-center" sizeClass="text-13">
            It looks like there aren't any new contacts to surface just yet.
            We'll email you or check back later as we continue refining your
            results!
            <br />
            Want more connections? Network Supercharge is an awesome way to
            build your network on Creatorland.
          </Text>
        )}

        {dealSyncing && (
          <div className="flex full-w justify-center items-center gap-8">
            <Image
              src="/assets/coin-icon.svg"
              alt="Loading"
              width={20}
              height={20}
              className={styles.spin}
            />
            <span className="font-bold text-14">
              Scanning the Creatorland matrix...
            </span>
          </div>
        )}
        {contacts.length > 0 && (
          <div className="flex flex-column gap-10 full-w pt-10">
            <ButtonComponent
              size="xSmall"
              fullWidth
              label={`Invite (${contacts.length}) VIPS to connect`}
              onClick={() => handleInviteContacts(contacts)}
            />
            <ButtonComponent
              size="xSmall"
              fullWidth
              label="View all recommended VIPS"
              iconLeft={
                <EyeIcon
                  style={{ width: 14, height: 14 }}
                  className="text-primary"
                />
              }
              variation="secondary"
              onClick={() => setContactsModalOpen(true)}
            />
          </div>
        )}
      </div>
    </>
  )
}
